// PropertySchematic — 2x2 Quadrant room potential grid
// High-value visual: rooms plotted by VALUE (y-axis) vs EFFORT (x-axis)

import { Maximize2, Home, ChefHat, Bath, DoorOpen, Layers } from "lucide-react";

function deriveRoomPotential(inputs, topStrategyId) {
  const rooms = [];
  const features = inputs?.features || [];
  const separateKitchen = inputs?.separate_kitchen;
  const bedrooms = inputs?.bedrooms;
  const bedroomCount = {"1": 1, "2": 2, "3": 3, "4_plus": 4}[String(bedrooms)] || 2;

  if (separateKitchen === "yes" || separateKitchen === "true") {
    rooms.push({ id: "kitchen", label: "Kitchen", value: "High", effort: "Med", icon: ChefHat });
  } else if (separateKitchen === "partial") {
    rooms.push({ id: "kitchen", label: "Kitchen", value: "Med", effort: "Low", icon: ChefHat });
  }

  if (features.includes("loft")) {
    rooms.push({ id: "loft", label: "Loft", value: "High", effort: "High", icon: Maximize2 });
  }

  if (features.includes("garage")) {
    rooms.push({ id: "garage", label: "Garage", value: "High", effort: "Med", icon: Home });
  }

  if (bedroomCount <= 2) {
    rooms.push({ id: "living", label: "Living Areas", value: "High", effort: "Med", icon: Layers });
  } else {
    rooms.push({ id: "living", label: "Living Areas", value: "Med", effort: "Low", icon: Layers });
  }

  const bathrooms = parseInt(inputs?.bathrooms) || 1;
  const expectedBaths = Math.max(1, Math.floor(bedroomCount / 2));
  if (bathrooms < expectedBaths) {
    rooms.push({ id: "bathroom", label: "Bathrooms", value: "High", effort: "Med", icon: Bath });
  } else {
    rooms.push({ id: "bathroom", label: "Bathrooms", value: "Low", effort: "Low", icon: Bath });
  }

  if (bedroomCount <= 3) {
    rooms.push({ id: "bedrooms", label: "Bedrooms", value: "Med", effort: "Low", icon: DoorOpen });
  }

  if (features.includes("conservatory")) {
    rooms.push({ id: "conservatory", label: "Conservatory", value: "Med", effort: "Med", icon: Home });
  }

  return rooms;
}

function getQuadrant(value, effort) {
  // value: High/Med/Low, effort: High/Med/Low
  // Quadrants: HV-LE (best), HV-HE, LV-LE, LV-HE (worst)
  const isHighValue = value === "High";
  const isLowEffort = effort === "Low" || effort === "Med";
  if (isHighValue && isLowEffort) return { q: "quick-win", color: "text-cipher-green", bg: "bg-cipher-green/15", label: "Quick Win" };
  if (isHighValue) return { q: "high-impact", color: "text-cipher-gold", bg: "bg-cipher-gold/15", label: "High Impact" };
  if (isLowEffort) return { q: "easy-gain", color: "text-blue-400", bg: "bg-blue-400/15", label: "Easy Gain" };
  return { q: "consider", color: "text-white/40", bg: "bg-white/5", label: "Consider" };
}

export default function PropertySchematic({ dwellingType, features, separateKitchen, bedrooms }) {
  const inputs = { features: features || [], separate_kitchen: separateKitchen, bedrooms, dwelling_type: dwellingType };
  const rooms = deriveRoomPotential(inputs);

  // Group by quadrant
  const quickWins = rooms.filter(r => getQuadrant(r.value, r.effort).q === "quick-win");
  const highImpact = rooms.filter(r => getQuadrant(r.value, r.effort).q === "high-impact");
  const easyGains = rooms.filter(r => getQuadrant(r.value, r.effort).q === "easy-gain");
  const consider = rooms.filter(r => getQuadrant(r.value, r.effort).q === "consider");

  const renderQuadrant = (title, items, colors) => (
    <div className={`rounded-lg p-2.5 ${colors.bg} min-h-[60px]`}>
      <p className={`text-[8px] font-semibold uppercase tracking-wider mb-1.5 ${colors.color}`}>{title}</p>
      {items.length > 0 ? (
        <div className="space-y-1">
          {items.map(r => {
            const Icon = r.icon;
            return (
              <div key={r.id} className="flex items-center gap-1.5">
                <Icon className={`w-3 h-3 ${colors.color} opacity-70`} />
                <span className="text-[10px] text-white/60">{r.label}</span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-[9px] text-white/20">—</p>
      )}
    </div>
  );

  return (
    <div data-testid="property-schematic">
      {/* Axis labels */}
      <div className="flex items-center mb-1">
        <div className="w-16" />
        <p className="flex-1 text-center text-[8px] text-white/25 uppercase tracking-wider">Lower Effort</p>
        <p className="flex-1 text-center text-[8px] text-white/25 uppercase tracking-wider">Higher Effort</p>
      </div>

      {/* Quadrant grid */}
      <div className="flex gap-0.5">
        {/* Y-axis label */}
        <div className="w-4 flex flex-col justify-around items-center py-2">
          <p className="text-[8px] text-white/25 uppercase tracking-wider" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>Higher Value</p>
        </div>

        <div className="flex-1 grid grid-cols-2 gap-1">
          {renderQuadrant("Quick Wins", quickWins, { color: "text-cipher-green", bg: "bg-cipher-green/10" })}
          {renderQuadrant("High Impact", highImpact, { color: "text-cipher-gold", bg: "bg-cipher-gold/8" })}
          {renderQuadrant("Easy Gains", easyGains, { color: "text-blue-400", bg: "bg-blue-400/8" })}
          {renderQuadrant("Consider", consider, { color: "text-white/40", bg: "bg-white/[0.03]" })}
        </div>
      </div>

      <p className="text-[8px] text-white/20 text-center mt-2">
        Rooms plotted by value potential vs implementation effort
      </p>
    </div>
  );
}
